import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import { displayToast } from "../../components/app/AppToast";
import { GptConversation } from "../../components/gpt/chat/GptConversation";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { Button } from "../../components/ui/Button";
import { ChatInput } from "../../components/ui/ChatInput";
import {
  returnToJuisciGptAfterSignIn,
  useDeviceFingerprint,
  type AnonymousGptOffer,
} from "../../domains/gpt/anonymous/anonymousGpt";
import { useAnonymousGptSession } from "../../domains/gpt/anonymous/useAnonymousGptSession";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { useCopyAnswer } from "../../domains/gpt/hooks/useCopyAnswer";
import type { AnonymousGptLimitKind } from "../../tools/analytics/domains/gpt.analytics";
import { useAppInstall } from "../app-install/AppInstall";

/**
 * JuisciGPT for a logged-out visitor (PRD "Juisci GPT sans compte"). Stands at
 * the front door in place of the sign-in gate on a desktop browser, and in place
 * of the install wall on mobile web (`useAnonymousGptFrontDoor`).
 *
 * One question, one answer, then the offer: the account is what buys the
 * follow-up, the saved thread and the feed. Each question is a session of its
 * own, and the visitor gets a couple a day (the API decides, the page reads
 * `/allowance`). Each answered question is saved under the visitor, and the
 * account they sign in to claims it (`claimAnonymousConversations`): the sign-in
 * lands on that conversation, so the answer they came for is in their history.
 *
 * On mobile web the offer is the app instead: the browser signs nobody in
 * there, and the app is where the account lives. The conversation stays with
 * the browser, because the app does not share its cookies.
 *
 * On a browser an account has signed in on, the page sends its owner to the
 * login page and offers no question.
 */
export default function GPTAnonymousChatPage({
  offer,
}: {
  offer: AnonymousGptOffer;
}): React.ReactElement {
  const fingerprint = useDeviceFingerprint();
  // The fingerprint is part of the question's identity: the chat cannot be
  // built before it is known.
  if (fingerprint.state === "pending")
    return <AnonymousShell onLogin={null}>{null}</AnonymousShell>;
  return <AnonymousChat device={fingerprint.value} offerKind={offer} />;
}

function AnonymousChat({
  device,
  offerKind,
}: {
  device: string | null;
  offerKind: AnonymousGptOffer;
}): React.ReactElement {
  const { t } = useTranslation();
  const copyAnswer = useCopyAnswer();
  const { scrollRef, isAtBottom, scrollToBottom } = useChatScrollAnchor({
    initialToBottom: false,
  });
  const session = useAnonymousGptSession(device);
  const { chat, question, refusal } = session;

  // An account signed in on this browser: its owner logs in, and lands back
  // here. Memorized while rendering, as the router's logged-out guard does.
  if (session.isMember) {
    returnToJuisciGptAfterSignIn();
    return (
      <Redirect
        to={{ pathname: "/login", state: { surface: "anonymous_gpt_member" } }}
      />
    );
  }

  const offer = (() => {
    switch (offerKind) {
      case "signup":
        return (
          <SignupOffer
            refusal={refusal}
            hasQuestion={question !== undefined}
            canAskAnother={session.canAskAnother}
            onSignup={session.openSignup}
            onAskAnother={session.askAnother}
          />
        );
      case "install":
        return (
          <InstallOffer
            refusal={refusal}
            canAskAnother={session.canAskAnother}
            onAskAnother={session.askAnother}
          />
        );
      default:
        throw new Error(offerKind satisfies never);
    }
  })();

  return (
    // Mobile web has no login: the app is where the account lives.
    <AnonymousShell onLogin={offerKind === "signup" ? session.openLogin : null}>
      {question === undefined ? (
        <Landing
          draft={session.draft}
          onDraft={session.setDraft}
          onAsk={session.ask}
          isAllowancePending={session.isAllowancePending}
          offer={refusal === null ? null : offer}
        />
      ) : (
        <ScrollableLayout
          containerStyle={{ background: "var(--color-ds-cream)" }}
          background="var(--color-ds-cream)"
          header={null}
          scrollRef={scrollRef}
          hideScrollbar
        >
          <GptConversation
            chat={chat}
            leading={null}
            headerActions={null}
            afterTurns={null}
            composerNotice={null}
            // A visitor has no feed to shape and no Room to join yet.
            suggestion={null}
            pushOptIn={null}
            attachments={null}
            canModifyAttachments={false}
            references={null}
            study={null}
            canModifyReferences={false}
            cwyopPaper={null}
            showModePicker={false}
            evidenceGraphByTurnIndex={new Map()}
            evidenceCheckByRunId={new Map()}
            onOpenEvidenceSource={() => undefined}
            pendingEvidence={null}
            searchByTurnIndex={new Map()}
            pendingSearch={null}
            onOpenSearchArticle={() => undefined}
            onRefineSearch={() => undefined}
            onAskGeneral={null}
            onAnswerCard={null}
            answerMath={false}
            actions={{
              onCopy: (asked, answer) => {
                copyAnswer(asked, answer, () => {
                  displayToast(t("Copied"), "success");
                });
              },
              onShare: () => undefined,
              showShare: false,
              onRegenerate: null,
              onRetry: session.canRetry ? session.retry : null,
              onEvaluate: null,
              evaluatedTurnIndexes: [],
              comingSoon: () => undefined,
            }}
            // The follow-up is what the account buys.
            followUps={null}
            disclaimer={t(
              "AI can make mistakes. Consider checking Source information."
            )}
            background="var(--color-ds-cream)"
            roomId={null}
            assistantConversationId={null}
            viewStateKey={`anonymous-${session.sessionKey}`}
            onOpenSources={() => undefined}
            onOpenSource={() => undefined}
            composer={
              session.isSpent || refusal !== null
                ? { kind: "cta", content: offer }
                : {
                    kind: "input",
                    onSubmit: session.ask,
                    onStop: () => {
                      void chat.stop();
                    },
                    placeholder: t("Ask a clinical question"),
                  }
            }
            isReconnecting={false}
            hasLostTurn={false}
            followKeyboard
            scrollRef={scrollRef}
            isAtBottom={isAtBottom}
            onScrollToBottom={scrollToBottom}
            revealAssistantId={null}
          />
        </ScrollableLayout>
      )}
    </AnonymousShell>
  );
}

function AnonymousShell({
  onLogin,
  children,
}: {
  // Null while the page is still reading the browser, where there is nothing
  // behind the door to return to yet, and on mobile web, which has no door.
  onLogin: (() => void) | null;
  children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col bg-ds-cream">
      {/* The desktop shell's sidebar already carries the logo: the header
          holds the door. */}
      <header className="flex items-center justify-end px-8 py-5">
        {onLogin === null ? null : (
          <Button variant="secondary" size="sm" onClick={onLogin}>
            {t("Log in")}
          </Button>
        )}
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}

function Landing({
  draft,
  onDraft,
  onAsk,
  isAllowancePending,
  offer,
}: {
  draft: string;
  onDraft: (value: string) => void;
  onAsk: (question: string) => void;
  // Nothing is offered until the allowance answers (`isAllowancePending`).
  isAllowancePending: boolean;
  // Non-null when today's questions are already used: the offer replaces the
  // composer before a question is typed for nothing.
  offer: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center gap-8 px-6 pb-24">
      <div className="flex flex-col gap-3 text-center">
        <h1 className="m-0 font-dm-sans text-heading-display font-semibold text-ds-black">
          {t("Ask a clinical question.")}
        </h1>
        <p className="m-0 font-dm-sans text-heading-xs text-ds-slate-700">
          {t(
            "JuisciGPT searches recent publications and guidelines, and links every claim to its source."
          )}
        </p>
      </div>
      {isAllowancePending ? null : offer === null ? (
        <div className="flex flex-col gap-3">
          <ChatInput
            className="w-full"
            placeholder={t("Ask a clinical question")}
            value={draft}
            onChange={(e) => {
              onDraft(e.target.value);
            }}
            mode={null}
            leading={null}
            leadingPanel={null}
            banner={null}
            attach={null}
            dictation={null}
            stream={null}
            onSend={() => {
              onAsk(draft.trim());
            }}
            sendDisabled={draft.trim() === ""}
            autoFocus
          />
          <p className="m-0 text-center font-dm-sans text-footnote text-ds-slate-600">
            {t(
              "Leave out anything that could identify a patient. AI can make mistakes and never replaces your clinical judgement."
            )}
          </p>
        </div>
      ) : (
        offer
      )}
    </div>
  );
}

/**
 * What follows the answer. The reason is stated plainly, and the network
 * ceiling, the one a hospital meets, never reads as a ban.
 */
function SignupOffer({
  refusal,
  hasQuestion,
  canAskAnother,
  onSignup,
  onAskAnother,
}: {
  refusal: AnonymousGptLimitKind | null;
  // False when the page opened on a ceiling already reached: there is no
  // question on screen to carry into the account.
  hasQuestion: boolean;
  canAskAnother: boolean;
  onSignup: () => void;
  onAskAnother: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-ds-slate-200 bg-ds-white p-6">
      <div className="flex flex-col gap-1">
        <p className="m-0 font-dm-sans text-heading-xs font-semibold text-ds-black">
          {refusalTitle(t, refusal)}
        </p>
        <p className="m-0 font-dm-sans text-footnote text-ds-slate-700">
          {hasQuestion
            ? t(
                "Create your account within 3 days and this question and its answer go into it. There you can follow up and get the week's papers in your topics."
              )
            : t(
                "With an account you can keep asking, follow up on an answer, save it and get the week's papers in your topics."
              )}
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button variant="primary" onClick={onSignup}>
          {t("Create a free account")}
        </Button>
        {canAskAnother && (
          <Button variant="secondary" onClick={onAskAnother}>
            {t("Ask another question")}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * What follows the answer on mobile web: the app, where the account lives.
 * The question does not follow the visitor there, so the copy promises only
 * what the app gives.
 */
function InstallOffer({
  refusal,
  canAskAnother,
  onAskAnother,
}: {
  refusal: AnonymousGptLimitKind | null;
  canAskAnother: boolean;
  onAskAnother: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const { isCopied, isInstallInProgress, install } = useAppInstall("inline");
  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-ds-slate-200 bg-ds-white p-6">
      <div className="flex flex-col gap-1">
        <p className="m-0 font-dm-sans text-heading-xs font-semibold text-ds-black">
          {installRefusalTitle(t, refusal)}
        </p>
        <p className="m-0 font-dm-sans text-footnote text-ds-slate-700">
          {t(
            "With the app you can keep asking, follow up on an answer, save it and get the week's papers in your topics."
          )}
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button
          variant="primary"
          onClick={install}
          disabled={isInstallInProgress || isCopied}
          aria-busy={isInstallInProgress}
        >
          {isCopied ? t("Content Copied") : t("Continue to the Juisci App")}
        </Button>
        {canAskAnother && (
          <Button variant="secondary" onClick={onAskAnother}>
            {t("Ask another question")}
          </Button>
        )}
      </div>
    </div>
  );
}

/** The titles of the install offer: the action is the app, not an account. */
function installRefusalTitle(
  t: (key: string) => string,
  refusal: AnonymousGptLimitKind | null
): string {
  switch (refusal) {
    case null:
      return t("Keep going in the app");
    case "visitor_daily":
      return t("You have used today's free questions");
    case "network_daily":
      return t(
        "Many people use Juisci from this network today. Install the app to continue."
      );
    case "capacity":
      return t(
        "Free questions are paused for today. Install the app to continue."
      );
    default:
      throw new Error(refusal satisfies never);
  }
}

function refusalTitle(
  t: (key: string) => string,
  refusal: AnonymousGptLimitKind | null
): string {
  switch (refusal) {
    case null:
      return t("Keep going with a free account");
    case "visitor_daily":
      return t("You have used today's free questions");
    case "network_daily":
      return t(
        "Many people use Juisci from this network today. Create an account to continue."
      );
    case "capacity":
      return t(
        "Free questions are paused for today. Create an account to continue."
      );
    default:
      throw new Error(refusal satisfies never);
  }
}
